import {create} from 'zustand';
import type {
  RoleWithPermissionsOut,
  TeamWithRolesOut,
  WhitelistedRouteOut,
  WhoamiResponse,
} from '@/lib/auth-types';
import {clearToken} from '@/lib/auth-token';
import {holdsPermission} from '@/lib/permissions';

interface AuthState {
  user: WhoamiResponse | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  roles: RoleWithPermissionsOut[];
  teams: TeamWithRolesOut[];
  whitelistedRoutes: WhitelistedRouteOut[];
  homePage: string;
  setUser: (user: WhoamiResponse) => void;
  clearUser: () => void;
}

const initialState = {
  user: null as WhoamiResponse | null,
  isAuthenticated: false,
  isSuperAdmin: false,
  roles: [] as RoleWithPermissionsOut[],
  teams: [] as TeamWithRolesOut[],
  whitelistedRoutes: [] as WhitelistedRouteOut[],
  homePage: '',
};

/**
 * A whoami payload with no `permissions` field at all can only come from an API
 * build predating DERP-249 — a current API sends `[]` for a user who genuinely
 * holds no keys. Left unsaid, that deployment mismatch is invisible: every
 * check answers false, which denies non-super-admins every gated route and
 * force-logs-out anyone whose `home_page` is one of them (the guard in
 * `_authenticated.tsx` clears the session when the redirect target is denied
 * too). What reaches support is "I can't log in", which does not name a cause.
 *
 * Warned on every whoami rather than once: the state is impossible against a
 * correct API, so the only way to see this twice is a genuinely broken
 * deployment, and a repeat line costs less than the module-level flag it would
 * take to suppress it. Deliberately not DEV-gated — production is where this
 * costs something.
 */
function warnIfPermissionsMissing(user: WhoamiResponse): void {
  if (user.permissions !== undefined) return;
  console.warn(
    '[auth] whoami returned no `permissions` field — this API build predates ' +
      'DERP-249. Every permission check will deny, so non-super-admin users ' +
      'lose access to all gated routes and UI. Deploy the API side first.',
  );
}

export const useAuthStore = create<AuthState>()((set) => ({
  ...initialState,

  setUser: (user: WhoamiResponse) => {
    warnIfPermissionsMissing(user);
    set({
      user,
      isAuthenticated: true,
      isSuperAdmin: user.is_super_admin,
      roles: user.roles ?? [],
      teams: user.teams ?? [],
      whitelistedRoutes: user.whitelisted_routes ?? [],
      homePage: user.home_page ?? '',
    });
  },

  clearUser: () => {
    clearToken();
    // IDB cleanup is fire-and-forget but errors are surfaced in dev. If the
    // delete fails (most commonly: another tab holds an open connection)
    // the worst case is that per-user offline data persists locally past
    // logout — a PDPA concern worth tracking, but not worth blocking the
    // logout flow over.
    void import('@/lib/offline/init')
      .then(({deleteOfflineData}) => deleteOfflineData())
      .catch((error) => {
        if (import.meta.env.DEV) {
          console.error('[auth] Failed to clean up offline data on logout:', error);
        }
      });
    set(initialState);
  },
}));

/**
 * Whether the signed-in user holds `resource:action`, as a subscription.
 *
 * Selects the *answer*, not a lookup function — that distinction is the whole
 * point (DERP-249). The store used to expose a `hasPermission` method, and
 * `useAuthStore((s) => s.hasPermission)` returns the same function identity on
 * every state change, so a component gated that way never re-rendered when the
 * user's permissions changed: the store knew the button should be gone and the
 * DOM kept showing it until something unrelated re-rendered. Selecting a
 * boolean means Zustand's equality check fires exactly when the answer flips,
 * which is what "permission changes take effect without re-login" requires.
 *
 * For the imperative case — a callback, an event handler, anything outside
 * render — read `user` from the store and call `holdsPermission` directly.
 */
export function usePermission(resource: string, action: string): boolean {
  return useAuthStore((s) => (s.user ? holdsPermission(s.user, resource, action) : false));
}

export function resetAuthStore() {
  useAuthStore.setState(initialState);
}
